import { memo, useMemo } from 'react';
import styled, { type CSSObject } from '@emotion/styled';

import type { ThemeProps } from '@emotion/react';

import { extractParamsFromMediaUrl } from '../../lib/mediaApiUrl';
import { themeFont } from '../../lib/themeFont';

import type { Wws_PhotoItem, Wws_PhotoTimelineItem } from '../../types/graphql';
import formatDateString from '../../lib/dateFormatter/formatDateString';
import DynamicNextImage from '../NextImage/DynamicNextImage';

const TimelineWrap = styled('div')`
  display: flex;
  margin: 24px auto 80px auto;
  max-width: 1000px;
  width: 100%;
  flex-direction: column;

  &::first-of-type {
    padding-top: 3rem;
  }
`;

const TimelinePhoto = styled('figure')`
  margin: 0;
  padding-left: 1rem;
  padding-right: 1rem;
  flex: 1;
  z-index: 1;
  padding-top: 1rem;
  padding-bottom: 1rem;
  width: 100%;

  background: ${({ theme }: ThemeProps): string => theme.colors.background};

  .secondaryPage &,
  .horizontalLayout & {
    @media (min-width: 900px) {
      padding-top: inherit;
      padding-bottom: inherit;
      background: transparent;
    }
  }

  .verticalLayout.homePage & {
    @media (min-width: 1300px) {
      padding-top: inherit;
      padding-bottom: inherit;
      background: transparent;
    }
  }
`;

const TimelineDetails = styled('div')`
  margin: 0;
  flex: 1;
  padding-left: 1rem;
  padding-right: 1rem;
  text-align: center;
  z-index: 1;
  padding-bottom: 1rem;
  background: ${({ theme }: ThemeProps): string => theme.colors.background};

  .secondaryPage &,
  .horizontalLayout & {
    @media (min-width: 900px) {
      text-align: inherit;
      padding-bottom: inherit;
      background: transparent;
    }
  }

  .verticalLayout.homePage & {
    @media (min-width: 1300px) {
      text-align: inherit;
      padding-bottom: inherit;
      background: transparent;
    }
  }

  ${themeFont('s6')}
`;

const TimelineDate = styled('h2')`
  font-weight: bold;

  ${themeFont('s4')}
`;

const TimelineItem = styled('div')`
  padding-bottom: 1.5rem;
  padding-top: 1.5rem;
  display: flex;
  align-items: center;
  position: relative;
  flex-direction: column;
  max-width: 100vw;
  gap: 1rem;

  .secondaryPage &,
  .horizontalLayout & {
    @media (min-width: 900px) {
      max-width: inherit;
      flex-direction: row;

      &:nth-of-type(odd) {
        flex-direction: row-reverse;
      }

      &::after {
        content: '';
        position: absolute;
        left: 50%;
        display: block;
        height: calc(50% - 0.6rem);
        bottom: 0;
        transform: translate(-50%, 0%);

        border-left: 1px dashed
          ${({ theme }: ThemeProps): string => theme.fonts.s4.color};

        ${({ theme }: ThemeProps): CSSObject | undefined =>
          theme.components.TimelineLineFlourish}
      }

      &::before {
        content: '';
        position: absolute;
        left: 50%;
        display: block;
        top: 0;
        height: calc(50% - 0.6rem);
        transform: translate(-50%, 0%);

        border-left: 1px dashed
          ${({ theme }: ThemeProps): string => theme.fonts.s4.color};

        ${({ theme }: ThemeProps): CSSObject | undefined =>
          theme.components.TimelineLineFlourish}
      }
    }
  }

  .verticalLayout.homePage & {
    @media (min-width: 1300px) {
      max-width: inherit;
      flex-direction: row;

      &:nth-of-type(odd) {
        flex-direction: row-reverse;
      }

      &::after {
        content: '';
        position: absolute;
        left: 50%;
        display: block;
        height: calc(50% - 0.6rem);
        bottom: 0;
        transform: translate(-50%, 0%);

        border-left: 1px dashed
          ${({ theme }: ThemeProps): string => theme.fonts.s4.color};

        ${({ theme }: ThemeProps): CSSObject | undefined =>
          theme.components.TimelineLineFlourish}
      }

      &::before {
        content: '';
        position: absolute;
        left: 50%;
        display: block;
        top: 0;
        height: calc(50% - 0.6rem);
        transform: translate(-50%, 0%);

        border-left: 1px dashed
          ${({ theme }: ThemeProps): string => theme.fonts.s4.color};

        ${({ theme }: ThemeProps): CSSObject | undefined =>
          theme.components.TimelineLineFlourish}
      }
    }
  }

  &::before {
    content: '';
    position: absolute;
    left: 50%;
    display: block;
    top: 0;
    bottom: 0;
    transform: translate(-50%, 0%);

    border-left: 1px dashed
      ${({ theme }: ThemeProps): string => theme.fonts.s4.color};

    ${({ theme }: ThemeProps): CSSObject | undefined =>
      theme.components.TimelineLineFlourish}
  }
`;

const Dot = styled('div')`
  content: '';
  position: absolute;
  left: 50%;
  top: 50%;
  display: block;
  width: 0.5rem;
  height: 0.5rem;
  transform: translate(-50%, -50%);

  background-color: ${({ theme }: ThemeProps): string =>
    theme.components.TimelineDotFlourish
      ? 'transparent'
      : theme.fonts.s4.color};

  border-radius: ${({ theme }: ThemeProps) =>
    theme.components.TimelineDotFlourish ? 'inherit' : '100rem'};

  ${({ theme }: ThemeProps): CSSObject | undefined =>
    theme.components.TimelineDotFlourish}
`;

function comparePhotos(
  firstEl: Wws_PhotoItem,
  secondEl: Wws_PhotoItem
): 0 | 1 | -1 {
  const firstElDate = firstEl.date || '';
  const secondElDate = secondEl.date || '';

  if (firstElDate < secondElDate) {
    return -1;
  }

  if (firstElDate > secondElDate) {
    return 1;
  }

  return 0;
}

interface PhotoTimelineItemProps {
  item: Wws_PhotoTimelineItem;
  className?: string | undefined;
}

function PhotoTimelineItem({ item, className }: PhotoTimelineItemProps) {
  const sortedPhotoItems = useMemo<Wws_PhotoItem[]>(() => {
    return [...(item.photoItems || [])].sort(comparePhotos);
  }, [item.photoItems]);

  return (
    <TimelineWrap
      className={className}
      data-testid={`photo-timeline-item-${item.id}`}
    >
      {sortedPhotoItems.map((photo: Wws_PhotoItem, index: number) => {
        const { crop } = extractParamsFromMediaUrl(photo.mediaUrl);

        return (
          <TimelineItem
            key={`photoTimeline-${photo.id}`}
            data-testid={`photo-timeline-row-${index}`}
          >
            <TimelinePhoto>
              <DynamicNextImage
                src={photo.mediaUrl || ''}
                alt={photo.caption || 'Memory'}
                crop={crop || ''}
                rotation={photo.rotation}
                height={photo.height || undefined}
                width={photo.width || undefined}
              />
            </TimelinePhoto>

            <TimelineDetails>
              {photo.date ? (
                <TimelineDate>{formatDateString(photo.date)}</TimelineDate>
              ) : null}
              {photo.caption ? <div>{photo.caption}</div> : null}
            </TimelineDetails>

            <Dot />
          </TimelineItem>
        );
      })}
    </TimelineWrap>
  );
}

export default memo(PhotoTimelineItem);
